import React, { useEffect } from "react"
import { CalculationSchema } from "src/features/calculator/schema"
import { UseFormSetValue, UseFormWatch, FieldErrors } from "react-hook-form"
import styled from "styled-components"
import {
  calcTotalReimbursement,
  calcSelfPayment,
  calcCustomerTransferFee,
  calcRemittance,
} from "src/features/calculator/utils/formulas/basicFormula"
import { TotalReimbursementRow } from "src/features/calculator/components/formula/row/TotalReimbursementRow"
import { SelfPaymentRow } from "src/features/calculator/components/formula/row/SelfPaymentRow"
import { CustomerTransferFeeRow } from "src/features/calculator/components/formula/row/CustomerTransferFeeRow"
import { RemittanceRow } from "src/features/calculator/components/formula/row/RemittanceRow"
import { useCalculatedResult } from "src/features/calculator/hooks/useCalculatedResult"
import { FormField } from "src/features/calculator/components/field/FormField"
import { calcCustomerTransferFeeRate } from "src/features/calculator/utils/formulas/discountFormula"
import { useFormValueUpdate } from "src/features/calculator/hooks/useFormValueUpdate"

type Props = {
  setValue: UseFormSetValue<CalculationSchema>
  watch: UseFormWatch<CalculationSchema>
  errors: FieldErrors<CalculationSchema>
}

export const DiscountFormula = ({ setValue, watch, errors }: Props) => {
  const watchedValues = watch()
  const {
    customerTransferFeeRate,
    totalReimbursement,
    customerTransferFee,
    consumptionTaxRate,
    maxAdvancePayment,
    initialCost,
    downPayment,
    rent,
    exDiscountedCustomerTransferFeeRate,
  } = watchedValues

  const calculatedResult = useCalculatedResult({
    values: watchedValues,
    setValue,
    calculation: {
      customerTransferFeeRate: calcCustomerTransferFeeRate({
        exDiscountedCustomerTransferFeeRate,
        consumptionTaxRate,
      }),
      totalReimbursement: calcTotalReimbursement({
        maxAdvancePayment,
        initialCost,
        downPayment,
      }),
      selfPayment: calcSelfPayment({
        maxAdvancePayment,
        initialCost,
        downPayment,
      }),
      customerTransferFee: calcCustomerTransferFee({
        rent,
        customerTransferFeeRate,
      }),
      remittance: calcRemittance({
        totalReimbursement,
        customerTransferFee,
      }),
    },
  })

  useFormValueUpdate({
    calculatedValue: calculatedResult.customerTransferFeeRate,
    currentValue: customerTransferFeeRate,
    fieldName: "customerTransferFeeRate",
    setValue,
  })

  useFormValueUpdate({
    calculatedValue: calculatedResult.totalReimbursement,
    currentValue: totalReimbursement,
    fieldName: "totalReimbursement",
    setValue,
  })

  useFormValueUpdate({
    calculatedValue: calculatedResult.customerTransferFee,
    currentValue: customerTransferFee,
    fieldName: "customerTransferFee",
    setValue,
  })

  return (
    <StyledCalculatorDiv>
      <StyledCalculatorRowDiv>
        <FormField
          label="送客手数料率(税込)"
          type="number"
          placeholder="Placeholder"
          hint={`${customerTransferFeeRate}%`}
          disabled
          value={customerTransferFeeRate}
        />
        <span>=</span>
        <FormField
          label="割引後送客手数料率(税抜)"
          type="number"
          placeholder="Placeholder"
          hint={`${exDiscountedCustomerTransferFeeRate}%`}
          onChange={(e) => {
            setValue("exDiscountedCustomerTransferFeeRate", Number(e.target.value))
          }}
          errorText={errors?.exDiscountedCustomerTransferFeeRate?.message}
          value={exDiscountedCustomerTransferFeeRate}
        />
        <span>x ( 1 +</span>
        <FormField
          label="消費税率"
          type="number"
          placeholder="Placeholder"
          hint={`${consumptionTaxRate}%`}
          onChange={(e) => {
            setValue("consumptionTaxRate", Number(e.target.value))
          }}
          errorText={errors?.consumptionTaxRate?.message}
          value={consumptionTaxRate}
        />
        <span>)</span>
      </StyledCalculatorRowDiv>
      <TotalReimbursementRow
        totalReimbursement={calculatedResult.totalReimbursement}
        maxAdvancePayment={maxAdvancePayment}
        initialCost={initialCost}
        downPayment={downPayment}
        setValue={setValue}
        errors={errors}
      />
      <SelfPaymentRow
        selfPayment={calculatedResult.selfPayment}
        maxAdvancePayment={maxAdvancePayment}
        initialCost={initialCost}
        downPayment={downPayment}
        setValue={setValue}
        errors={errors}
      />
      <CustomerTransferFeeRow
        customerTransferFee={calculatedResult.customerTransferFee}
        rent={rent}
        customerTransferFeeRate={customerTransferFeeRate}
        setValue={setValue}
        errors={errors}
      />
      <RemittanceRow
        remittance={calculatedResult.remittance}
        totalReimbursement={totalReimbursement}
        customerTransferFee={customerTransferFee}
        setValue={setValue}
        errors={errors}
      />
    </StyledCalculatorDiv>
  )
}

const StyledCalculatorDiv = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const StyledCalculatorRowDiv = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
`
